import React, { useState, useEffect, useRef } from 'react';
import {
  collection, onSnapshot, doc, updateDoc, addDoc,
  serverTimestamp, query, orderBy, getDoc,
} from 'firebase/firestore';
import { db, auth } from '../firebase';

const STATUS_COLORS = {
  pending:   { bg: 'rgba(239,68,68,0.1)',   border: 'rgba(239,68,68,0.3)',   text: '#EF4444' },
  reviewed:  { bg: 'rgba(59,130,246,0.1)',  border: 'rgba(59,130,246,0.3)',  text: '#3B82F6' },
  actioned:  { bg: 'rgba(34,197,94,0.1)',   border: 'rgba(34,197,94,0.3)',   text: '#22C55E' },
  dismissed: { bg: 'rgba(100,100,100,0.1)', border: 'rgba(100,100,100,0.3)', text: '#888' },
};

const StatusBadge = ({ status }) => {
  const c = STATUS_COLORS[status] || STATUS_COLORS.dismissed;
  const labels = { pending: '🚨 Pending', reviewed: '🔍 Reviewed', actioned: '✅ Actioned', dismissed: '✖ Dismissed' };
  return (
    <span style={{ background: c.bg, border: `1px solid ${c.border}`, color: c.text, borderRadius: 20, padding: '3px 10px', fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap' }}>
      {labels[status] || status}
    </span>
  );
};

const RoleBadge = ({ role }) => (
  <span style={{
    background: role === 'driver' ? 'rgba(249,115,22,0.15)' : 'rgba(59,130,246,0.15)',
    color: role === 'driver' ? '#F97316' : '#3B82F6',
    border: `1px solid ${role === 'driver' ? 'rgba(249,115,22,0.3)' : 'rgba(59,130,246,0.3)'}`,
    borderRadius: 6, padding: '2px 8px', fontSize: 11, fontWeight: 700, textTransform: 'uppercase',
  }}>
    {role === 'driver' ? 'DRIVER' : 'HIRER'}
  </span>
);

const CopyButton = ({ text }) => {
  const [copied, setCopied] = useState(false);
  const handleCopy = (e) => {
    e.stopPropagation();
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <button
      onClick={handleCopy}
      style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 6, padding: '2px 8px', fontSize: 11, color: copied ? '#22C55E' : 'var(--text-secondary)', cursor: 'pointer', transition: 'color 0.15s', flexShrink: 0 }}
    >
      {copied ? 'Copied!' : 'Copy'}
    </button>
  );
};

const InfoField = ({ label, value }) => (
  <div>
    <div style={{ color: 'var(--text-muted)', fontSize: 11, marginBottom: 3 }}>{label}</div>
    <div style={{ color: 'var(--text)', fontSize: 13 }}>{value || '—'}</div>
  </div>
);

const timeAgo = (ts) => {
  if (!ts?.seconds) return '—';
  const diff = Math.floor((Date.now() / 1000 - ts.seconds) / 60);
  if (diff < 60) return `${diff}m ago`;
  if (diff < 1440) return `${Math.floor(diff / 60)}h ago`;
  return `${Math.floor(diff / 1440)}d ago`;
};

const formatDate = (ts) => {
  if (!ts?.seconds) return '—';
  return new Date(ts.seconds * 1000).toLocaleString('en-GB', {
    day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
};

const accountStatusColor = (s) => {
  if (s === 'active') return '#22C55E';
  if (s === 'suspended') return '#EF4444';
  if (s === 'warned') return '#EAB308';
  return 'var(--text-secondary)';
};

const SECTION_LABEL = {
  color: 'var(--text-muted)', fontSize: 11, textTransform: 'uppercase',
  letterSpacing: 1, fontWeight: 700, marginBottom: 10,
};

const DIVIDER = { height: 1, background: 'var(--surface2)', margin: '0 0 16px' };

const FILTERS = [
  { id: 'pending',   label: '🚨 Pending' },
  { id: 'reviewed',  label: '🔍 Reviewed' },
  { id: 'actioned',  label: '✅ Actioned' },
  { id: 'all',       label: '📋 All' },
];

export default function ReportsPage() {
  const [reports, setReports]             = useState([]);
  const [loading, setLoading]             = useState(true);
  const [filter, setFilter]               = useState('pending');
  const [expanded, setExpanded]           = useState(null);
  const [adminNotes, setAdminNotes]       = useState('');
  const [saving, setSaving]               = useState(false);
  const [newCount, setNewCount]           = useState(0);
  const [userDataCache, setUserDataCache] = useState({});
  const initialLoaded                     = useRef(false);
  const prevPendingCount                  = useRef(0);
  const fetchingRef                       = useRef(new Set());

  useEffect(() => {
    const q = query(collection(db, 'reports'), orderBy('createdAt', 'desc'));
    const unsub = onSnapshot(q, (snap) => {
      const data = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      const pending = data.filter(r => r.status === 'pending').length;

      if (initialLoaded.current) {
        const diff = pending - prevPendingCount.current;
        if (diff > 0) setNewCount(n => n + diff);
      } else {
        initialLoaded.current = true;
        setLoading(false);
      }
      prevPendingCount.current = pending;
      setReports(data);
      setLoading(false);
    });
    return unsub;
  }, []);

  const filtered = reports.filter(r => {
    if (filter === 'pending')  return r.status === 'pending';
    if (filter === 'reviewed') return r.status === 'reviewed';
    if (filter === 'actioned') return r.status === 'actioned' || r.status === 'dismissed';
    return true;
  });

  const pendingCount = reports.filter(r => r.status === 'pending').length;

  const handleExpand = async (report) => {
    if (expanded?.id === report.id) {
      setExpanded(null);
      setAdminNotes('');
      return;
    }
    setExpanded(report);
    setAdminNotes(report.adminNotes || '');

    if (userDataCache[report.id] || fetchingRef.current.has(report.id)) return;
    fetchingRef.current.add(report.id);

    try {
      const [rSnap, dSnap] = await Promise.all([
        report.reporterId    ? getDoc(doc(db, 'users', report.reporterId))    : Promise.resolve(null),
        report.reportedUserId ? getDoc(doc(db, 'users', report.reportedUserId)) : Promise.resolve(null),
      ]);

      let jobData = null;
      if (report.jobId) {
        const jobSnap = await getDoc(doc(db, 'jobs', report.jobId));
        jobData = jobSnap.exists() ? jobSnap.data() : null;
      }

      setUserDataCache(prev => ({
        ...prev,
        [report.id]: {
          reporterData: rSnap?.exists() ? rSnap.data() : null,
          reportedData: dSnap?.exists() ? dSnap.data() : null,
          jobData,
        },
      }));
    } catch (err) {
      console.error('Failed to fetch user details:', err);
      setUserDataCache(prev => ({
        ...prev,
        [report.id]: { reporterData: null, reportedData: null, jobData: null },
      }));
    } finally {
      fetchingRef.current.delete(report.id);
    }
  };

  const updateReport = async (reportId, updates) => {
    setSaving(true);
    try {
      await updateDoc(doc(db, 'reports', reportId), {
        ...updates,
        reviewedAt: serverTimestamp(),
        reviewedBy: auth.currentUser?.uid || 'admin',
        adminNotes: adminNotes || expanded?.adminNotes || '',
      });
      setExpanded(null);
      setAdminNotes('');
    } catch (err) {
      console.error('Failed to update report:', err);
      alert('Error updating report. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleMarkReviewed = () => {
    if (!expanded) return;
    updateReport(expanded.id, { status: 'reviewed' });
  };

  const handleWarnUser = async () => {
    if (!expanded) return;
    const name = expanded.reportedName || expanded.reportedUserName || 'this user';
    if (!window.confirm(`Send an account warning to ${name}?`)) return;
    setSaving(true);
    try {
      await updateDoc(doc(db, 'reports', expanded.id), {
        status: 'actioned', actionTaken: 'warned',
        reviewedAt: serverTimestamp(),
        reviewedBy: auth.currentUser?.uid || 'admin',
        adminNotes: adminNotes || expanded.adminNotes || '',
      });
      await addDoc(collection(db, 'notifications'), {
        userId:    expanded.reportedUserId,
        title:     '⚠️ Account Warning',
        body:      'Your account has received a warning following a review of reported behaviour on Crewboard. Further violations may result in suspension.',
        type:      'account_warning',
        read:      false,
        createdAt: serverTimestamp(),
      });
      setExpanded(null);
      setAdminNotes('');
    } catch (err) {
      console.error('Failed to warn user:', err);
      alert('Error sending warning. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleSuspendUser = async () => {
    if (!expanded) return;
    const name = expanded.reportedName || expanded.reportedUserName || 'this user';
    if (!window.confirm(`Suspend ${name}? They will be locked out of the app.`)) return;
    setSaving(true);
    try {
      await updateDoc(doc(db, 'users', expanded.reportedUserId), {
        accountStatus:   'suspended',
        suspendedAt:     serverTimestamp(),
        suspendedReason: 'Suspended following report review by admin',
      });
      await updateDoc(doc(db, 'reports', expanded.id), {
        status: 'actioned', actionTaken: 'suspended',
        reviewedAt: serverTimestamp(),
        reviewedBy: auth.currentUser?.uid || 'admin',
        adminNotes: adminNotes || expanded.adminNotes || '',
      });
      setExpanded(null);
      setAdminNotes('');
    } catch (err) {
      console.error('Failed to suspend user:', err);
      alert('Error suspending user. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleDismiss = () => {
    if (!expanded) return;
    updateReport(expanded.id, { status: 'dismissed', actionTaken: 'none' });
  };

  const handleSaveNotes = async () => {
    if (!expanded) return;
    setSaving(true);
    try {
      await updateDoc(doc(db, 'reports', expanded.id), { adminNotes });
      setExpanded(prev => ({ ...prev, adminNotes }));
    } catch (err) {
      console.error('Failed to save notes:', err);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return (
    <div style={{ color: 'var(--text-muted)', padding: 40, textAlign: 'center' }}>Loading reports…</div>
  );

  return (
    <div className="animate-fade">
      <div style={{ marginBottom: 28 }}>
        <h1 style={{ fontFamily: 'Syne', fontSize: 28, fontWeight: 800, color: 'var(--text)' }}>Reports Queue</h1>
        <p style={{ color: 'var(--text-muted)', marginTop: 4 }}>
          Review user-submitted reports and take action
        </p>
      </div>

      {newCount > 0 && (
        <div style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 10, padding: '12px 16px', marginBottom: 20, display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ fontSize: 18 }}>🚨</span>
          <span style={{ color: '#EF4444', fontWeight: 600, flex: 1 }}>
            {newCount} new report{newCount > 1 ? 's' : ''} arrived while you were reviewing
          </span>
          <button
            onClick={() => { setNewCount(0); setFilter('pending'); }}
            style={{ background: '#EF4444', color: '#fff', border: 'none', borderRadius: 8, padding: '6px 14px', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}
          >
            View →
          </button>
        </div>
      )}

      <div style={{ display: 'flex', gap: 4, marginBottom: 24, background: 'var(--surface)', borderRadius: 12, padding: 4, width: 'fit-content', border: '1px solid var(--surface2)' }}>
        {FILTERS.map(f => {
          const isActive = filter === f.id;
          return (
            <button
              key={f.id}
              onClick={() => setFilter(f.id)}
              style={{
                background: isActive ? (f.id === 'pending' ? '#EF4444' : '#F97316') : 'transparent',
                border: 'none',
                color: isActive ? '#fff' : 'var(--text-secondary)',
                borderRadius: 8, padding: '8px 16px', fontSize: 13, fontWeight: 600,
                cursor: 'pointer', transition: 'all 0.15s', display: 'flex', alignItems: 'center', gap: 6,
              }}
            >
              {f.label}
              {f.id === 'pending' && pendingCount > 0 && (
                <span style={{ background: isActive ? 'rgba(255,255,255,0.3)' : '#EF4444', color: '#fff', borderRadius: 20, padding: '1px 7px', fontSize: 11, fontWeight: 700 }}>
                  {pendingCount}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {filtered.length === 0 ? (
        <div style={{ background: 'var(--surface)', border: '1px solid var(--surface2)', borderRadius: 16, padding: 48, textAlign: 'center' }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>{filter === 'pending' ? '✅' : '📋'}</div>
          <div style={{ color: 'var(--text)', fontWeight: 600 }}>
            {filter === 'pending' ? 'No pending reports' : 'No reports here'}
          </div>
          <div style={{ color: 'var(--text-muted)', fontSize: 13, marginTop: 4 }}>
            {filter === 'pending' ? 'All caught up!' : 'Try a different filter'}
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {filtered.map(report => {
            const isExpanded      = expanded?.id === report.id;
            const reportedRole    = report.reportedRole || report.reportedUserRole;
            const reportedName    = report.reportedName || report.reportedUserName || 'Unknown User';
            const userData        = userDataCache[report.id];
            const isLoadingUsers  = isExpanded && !userData;

            return (
              <div
                key={report.id}
                style={{
                  background: 'var(--surface)',
                  border: `1px solid ${isExpanded ? 'rgba(249,115,22,0.4)' : 'var(--surface2)'}`,
                  borderRadius: 14,
                  overflow: 'hidden',
                  transition: 'border-color 0.15s',
                }}
              >
                {/* ── Collapsed header ── */}
                <div
                  onClick={() => handleExpand(report)}
                  style={{ padding: '18px 20px', cursor: 'pointer', display: 'flex', gap: 16, alignItems: 'flex-start' }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    {/* Category + status badge */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 6 }}>
                      <span style={{ fontWeight: 700, color: 'var(--text)', fontSize: 15 }}>
                        🚨 {report.category || 'General Report'}
                      </span>
                      <StatusBadge status={report.status} />
                    </div>
                    {/* Reported user row */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, flexWrap: 'wrap' }}>
                      <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>Reported:</span>
                      <span style={{ color: 'var(--text)', fontSize: 13, fontWeight: 600 }}>{reportedName}</span>
                      {reportedRole && <RoleBadge role={reportedRole} />}
                    </div>
                    {/* Reporter row */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                      <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>By:</span>
                      {report.reporterRole && <RoleBadge role={report.reporterRole} />}
                    </div>
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 8, flexShrink: 0 }}>
                    <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>{timeAgo(report.createdAt)}</span>
                    <span style={{ color: 'var(--text-muted)', fontSize: 18, transition: 'transform 0.15s', transform: isExpanded ? 'rotate(180deg)' : 'none' }}>⌄</span>
                  </div>
                </div>

                {/* ── Expanded view ── */}
                {isExpanded && (
                  <div
                    style={{ borderTop: '1px solid var(--surface2)', padding: '20px 20px 24px', display: 'flex', flexDirection: 'column', gap: 0 }}
                    onClick={e => e.stopPropagation()}
                  >
                    {/* REPORTED USER */}
                    <div style={{ marginBottom: 16 }}>
                      <div style={SECTION_LABEL}>Reported User</div>
                      {isLoadingUsers ? (
                        <div style={{ color: 'var(--text-muted)', fontSize: 13, padding: '8px 0' }}>Loading user details…</div>
                      ) : (
                        <div style={{ background: 'var(--bg)', borderRadius: 10, padding: '14px 16px', display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 12 }}>
                          <InfoField
                            label="Name"
                            value={userData?.reportedData?.fullName || userData?.reportedData?.companyName || reportedName}
                          />
                          <InfoField
                            label="Role"
                            value={reportedRole ? <RoleBadge role={reportedRole} /> : '—'}
                          />
                          {userData?.reportedData?.email && (
                            <InfoField label="Email" value={userData.reportedData.email} />
                          )}
                          {userData?.reportedData?.accountStatus && (
                            <InfoField label="Account Status" value={
                              <span style={{ color: accountStatusColor(userData.reportedData.accountStatus), fontWeight: 600, textTransform: 'capitalize' }}>
                                {userData.reportedData.accountStatus}
                              </span>
                            } />
                          )}
                          {report.reportedUserId && (
                            <InfoField label="User ID" value={
                              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                <span style={{ fontFamily: 'monospace', fontSize: 12, color: 'var(--text-secondary)' }}>
                                  {report.reportedUserId.slice(0, 14)}…
                                </span>
                                <CopyButton text={report.reportedUserId} />
                              </div>
                            } />
                          )}
                        </div>
                      )}
                    </div>

                    <div style={DIVIDER} />

                    {/* REPORTED BY */}
                    <div style={{ marginBottom: 16 }}>
                      <div style={{ ...SECTION_LABEL, display: 'flex', alignItems: 'center', gap: 8 }}>
                        Reported By
                        <span style={{ background: 'rgba(239,68,68,0.1)', color: '#EF4444', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 4, padding: '1px 6px', fontSize: 10, fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase' }}>
                          Admin only
                        </span>
                      </div>
                      {isLoadingUsers ? (
                        <div style={{ color: 'var(--text-muted)', fontSize: 13, padding: '8px 0' }}>Loading…</div>
                      ) : (
                        <div style={{ background: 'var(--bg)', borderRadius: 10, padding: '14px 16px', display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 12 }}>
                          <InfoField
                            label="Name"
                            value={userData?.reporterData?.fullName || userData?.reporterData?.companyName || '—'}
                          />
                          <InfoField
                            label="Role"
                            value={report.reporterRole ? <RoleBadge role={report.reporterRole} /> : '—'}
                          />
                          {userData?.reporterData?.email && (
                            <InfoField label="Email" value={userData.reporterData.email} />
                          )}
                          {report.reporterId && (
                            <InfoField label="User ID" value={
                              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                <span style={{ fontFamily: 'monospace', fontSize: 12, color: 'var(--text-secondary)' }}>
                                  {report.reporterId.slice(0, 14)}…
                                </span>
                                <CopyButton text={report.reporterId} />
                              </div>
                            } />
                          )}
                        </div>
                      )}
                    </div>

                    <div style={DIVIDER} />

                    {/* REPORT DETAILS */}
                    <div style={{ marginBottom: 16 }}>
                      <div style={SECTION_LABEL}>Report Details</div>
                      <div style={{ background: 'var(--bg)', borderRadius: 10, padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 12 }}>
                        <InfoField label="Category" value={report.category || '—'} />
                        <InfoField label="Description" value={
                          <div style={{ color: 'var(--text)', fontSize: 13, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                            {report.description || 'No description provided'}
                          </div>
                        } />
                        {report.jobId && (
                          <InfoField label="Job ID" value={
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                              <span style={{ fontFamily: 'monospace', fontSize: 12, color: 'var(--text-secondary)' }}>{report.jobId}</span>
                              {userData?.jobData?.title && (
                                <span style={{ color: 'var(--text)', fontSize: 13 }}>— {userData.jobData.title}</span>
                              )}
                              <CopyButton text={report.jobId} />
                            </div>
                          } />
                        )}
                        <InfoField label="Submitted" value={formatDate(report.createdAt)} />
                      </div>
                    </div>

                    <div style={DIVIDER} />

                    {/* ADMIN NOTES */}
                    <div style={{ marginBottom: 16 }}>
                      <div style={SECTION_LABEL}>Admin Notes</div>
                      <textarea
                        value={adminNotes}
                        onChange={e => setAdminNotes(e.target.value)}
                        placeholder="Add internal notes about this report…"
                        rows={3}
                        style={{
                          width: '100%', background: 'var(--bg)', border: '1px solid var(--border)',
                          borderRadius: 8, padding: '10px 14px', color: 'var(--text)', fontSize: 13,
                          resize: 'vertical', outline: 'none', fontFamily: 'DM Sans', boxSizing: 'border-box',
                        }}
                      />
                      <button
                        onClick={handleSaveNotes}
                        disabled={saving}
                        style={{ marginTop: 8, background: 'var(--surface2)', border: '1px solid var(--border)', color: 'var(--text-secondary)', borderRadius: 8, padding: '6px 14px', fontSize: 12, fontWeight: 600, cursor: saving ? 'not-allowed' : 'pointer' }}
                      >
                        {saving ? 'Saving…' : 'Save Notes'}
                      </button>
                    </div>

                    <div style={DIVIDER} />

                    {/* ACTIONS */}
                    {(report.status === 'pending' || report.status === 'reviewed') && (
                      <div>
                        <div style={SECTION_LABEL}>Actions</div>
                        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                          {report.status === 'pending' && (
                            <button
                              onClick={handleMarkReviewed}
                              disabled={saving}
                              style={{ background: 'rgba(59,130,246,0.12)', border: '1px solid rgba(59,130,246,0.4)', color: '#3B82F6', borderRadius: 8, padding: '9px 16px', fontSize: 13, fontWeight: 600, cursor: saving ? 'not-allowed' : 'pointer' }}
                            >
                              🔍 Mark Reviewed
                            </button>
                          )}
                          <button
                            onClick={handleWarnUser}
                            disabled={saving}
                            style={{ background: 'rgba(234,179,8,0.12)', border: '1px solid rgba(234,179,8,0.4)', color: '#EAB308', borderRadius: 8, padding: '9px 16px', fontSize: 13, fontWeight: 600, cursor: saving ? 'not-allowed' : 'pointer' }}
                          >
                            ⚠️ Warn User
                          </button>
                          <button
                            onClick={handleSuspendUser}
                            disabled={saving}
                            style={{ background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.4)', color: '#EF4444', borderRadius: 8, padding: '9px 16px', fontSize: 13, fontWeight: 600, cursor: saving ? 'not-allowed' : 'pointer' }}
                          >
                            ⛔ Suspend User
                          </button>
                          <button
                            onClick={handleDismiss}
                            disabled={saving}
                            style={{ background: 'var(--surface2)', border: '1px solid var(--border)', color: 'var(--text-secondary)', borderRadius: 8, padding: '9px 16px', fontSize: 13, fontWeight: 600, cursor: saving ? 'not-allowed' : 'pointer' }}
                          >
                            ✖ Dismiss
                          </button>
                        </div>
                      </div>
                    )}

                    {report.status !== 'pending' && report.status !== 'reviewed' && (
                      <div style={{ background: 'rgba(34,197,94,0.06)', border: '1px solid rgba(34,197,94,0.2)', borderRadius: 8, padding: '10px 14px' }}>
                        <span style={{ color: '#22C55E', fontSize: 13, fontWeight: 600 }}>
                          ✅ This report has been actioned
                          {report.actionTaken && report.actionTaken !== 'none' ? ` (${report.actionTaken})` : ''}
                          {report.reviewedBy ? ` · by ${report.reviewedBy.slice(0, 8)}…` : ''}
                        </span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
